import { useEffect, useState, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import axios from "axios";
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import authConfig from "../../authConfig";
import Table from 'react-bootstrap/Table';
import Spinner from 'react-bootstrap/Spinner';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import InputGroup from 'react-bootstrap/InputGroup';
import './DecisionsPage.css';

export default function DecisionsPage() {
    const { t } = useTranslation();
    const language = useSelector((state) => state.language.value);
    const navigate = useNavigate();

    const [decisions, setDecisions] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filter state
    const [filterCommittee, setFilterCommittee] = useState('');
    const [filterYearFrom, setFilterYearFrom] = useState('');
    const [filterYearTo, setFilterYearTo] = useState('');
    const [filterText, setFilterText] = useState('');

    useEffect(() => {
        axios.get(authConfig.api.urls.getMeetingDecisions)
            .then((res) => {
                setDecisions(res.data);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    }, []);

    function formatDate(dateStr) {
        const d = new Date(dateStr);
        return d.toLocaleDateString(language === 'cy' ? 'cy-GB' : 'en-GB', {
            day: '2-digit', month: 'short', year: 'numeric'
        });
    }

    function getYear(dateStr) {
        return new Date(dateStr).getFullYear();
    }

    // Unique committee names for dropdown (language-aware)
    const committeeOptions = useMemo(() => {
        const names = decisions.map(d =>
            language === 'cy' ? (d.committeeNameCy || d.committeeNameEn) : d.committeeNameEn
        );
        return [...new Set(names)].sort();
    }, [decisions, language]);

    // Unique years for dropdown
    const yearOptions = useMemo(() => {
        const years = decisions.map(d => getYear(d.meetingDate));
        return [...new Set(years)].sort((a, b) => a - b);
    }, [decisions]);

    // Strip HTML tags for plain-text searching inside decision content
    function stripHtml(html) {
        if (!html) return '';
        return html.replace(/<[^>]*>/g, ' ');
    }

    // Apply all filters
    const filtered = useMemo(() => {
        const lowerText = filterText.trim().toLowerCase();
        return decisions.filter(d => {
            const committeeName = language === 'cy'
                ? (d.committeeNameCy || d.committeeNameEn)
                : d.committeeNameEn;
            const year = getYear(d.meetingDate);

            if (filterCommittee && committeeName !== filterCommittee) return false;
            if (filterYearFrom && year < parseInt(filterYearFrom)) return false;
            if (filterYearTo && year > parseInt(filterYearTo)) return false;

            if (lowerText) {
                const agendaName = language === 'cy'
                    ? (d.agendaNameCy || d.agendaNameEn)
                    : d.agendaNameEn;
                const decisionText = language === 'cy'
                    ? stripHtml(d.decisionCy || d.decisionEn)
                    : stripHtml(d.decisionEn);
                const searchable = [agendaName, decisionText, committeeName, d.agendaTagEn, d.agendaTagCy]
                    .join(' ')
                    .toLowerCase();
                if (!searchable.includes(lowerText)) return false;
            }

            return true;
        });
    }, [decisions, filterCommittee, filterYearFrom, filterYearTo, filterText, language]);

    function resetFilters() {
        setFilterCommittee('');
        setFilterYearFrom('');
        setFilterYearTo('');
        setFilterText('');
    }

    const hasActiveFilters = filterCommittee || filterYearFrom || filterYearTo || filterText.trim();

    // ── Shared cell renderers ──
    function committeeCell(d) {
        return language === 'cy' ? (d.committeeNameCy || d.committeeNameEn) : d.committeeNameEn;
    }

    function itemCell(d) {
        const tag  = language === 'cy' ? (d.agendaTagCy  || d.agendaTagEn)  : d.agendaTagEn;
        const name = language === 'cy' ? (d.agendaNameCy || d.agendaNameEn) : d.agendaNameEn;
        return <><span className="decisions-tag">{tag}</span>{name}</>;
    }

    function decisionHtml(d) {
        return language === 'cy' ? (d.decisionCy || d.decisionEn) : d.decisionEn;
    }

    return (
        <>
            <h1>{t("Decisions")}</h1>

            {/* ── Filters ── */}
            {!loading && decisions.length > 0 && (
                <div className="decisions-filters">
                    <Row className="g-2 align-items-end">

                        {/* Text search */}
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-1">{t("Search")}</Form.Label>
                            <InputGroup size="sm">
                                <Form.Control
                                    type="text"
                                    placeholder={t("Search decisions...")}
                                    value={filterText}
                                    onChange={e => setFilterText(e.target.value)}
                                />
                                {filterText && (
                                    <Button variant="outline-secondary" size="sm" onClick={() => setFilterText('')}>
                                        ✕
                                    </Button>
                                )}
                            </InputGroup>
                        </Col>

                        {/* Committee */}
                        <Col xs={12} sm={6} md={3}>
                            <Form.Label className="small mb-1">{t("Committee")}</Form.Label>
                            <Form.Select
                                size="sm"
                                value={filterCommittee}
                                onChange={e => setFilterCommittee(e.target.value)}
                            >
                                <option value="">{t("All Committees")}</option>
                                {committeeOptions.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </Form.Select>
                        </Col>

                        {/* Year from */}
                        <Col xs={6} sm={3} md={2}>
                            <Form.Label className="small mb-1">{t("Year from")}</Form.Label>
                            <Form.Select
                                size="sm"
                                value={filterYearFrom}
                                onChange={e => setFilterYearFrom(e.target.value)}
                            >
                                <option value="">{t("Any")}</option>
                                {yearOptions.map(y => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </Form.Select>
                        </Col>

                        {/* Year to */}
                        <Col xs={6} sm={3} md={2}>
                            <Form.Label className="small mb-1">{t("Year to")}</Form.Label>
                            <Form.Select
                                size="sm"
                                value={filterYearTo}
                                onChange={e => setFilterYearTo(e.target.value)}
                            >
                                <option value="">{t("Any")}</option>
                                {yearOptions.map(y => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </Form.Select>
                        </Col>

                        {/* Reset button */}
                        <Col xs={12} sm={12} md={1} className="d-flex align-items-end">
                            {hasActiveFilters && (
                                <Button variant="outline-danger" size="sm" onClick={resetFilters} className="w-100">
                                    {t("Reset")}
                                </Button>
                            )}
                        </Col>

                    </Row>
                </div>
            )}

            {/* ── Main content ── */}
            {loading ? (
                <div className="text-center mt-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">{t("Loading...")}</span>
                    </Spinner>
                </div>
            ) : decisions.length === 0 ? (
                <p className="decisions-no-results">{t("No decisions found.")}</p>
            ) : (
                <>
                    {/* Result count */}
                    <div className="decisions-result-count">
                        {hasActiveFilters
                            ? t("Showing {{count}} of {{total}} decisions", { count: filtered.length, total: decisions.length })
                            : t("{{count}} decisions", { count: filtered.length })
                        }
                    </div>

                    {filtered.length === 0 ? (
                        <p className="decisions-no-results">{t("No decisions match your filters.")}</p>
                    ) : (
                        <>
                            {/* ── Desktop: table (md and above) ── */}
                            <div className="d-none d-md-block">
                                <Table bordered hover className="decisions-table">
                                    <thead className="table-dark">
                                        <tr>
                                            <th className="decisions-date-cell">{t("Date")}</th>
                                            <th className="decisions-committee-cell">{t("Committee")}</th>
                                            <th>{t("Item")}</th>
                                            <th>{t("Decision")}</th>
                                            <th style={{ width: '90px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map((d, i) => (
                                            <tr key={i}>
                                                <td className="decisions-date-cell">{formatDate(d.meetingDate)}</td>
                                                <td className="decisions-committee-cell">{committeeCell(d)}</td>
                                                <td>{itemCell(d)}</td>
                                                <td dangerouslySetInnerHTML={{ __html: decisionHtml(d) }} />
                                                <td className="text-center align-middle">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => navigate('/meeting/' + d.meetingId)}
                                                        title={t("Open Meeting")}
                                                    >
                                                        {t("Open")}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>

                            {/* ── Mobile: cards (below md) ── */}
                            <div className="d-md-none">
                                {filtered.map((d, i) => (
                                    <div className="decision-card" key={i}>
                                        <div className="decision-card-meta">
                                            <span className="decision-card-date">📅 {formatDate(d.meetingDate)}</span>
                                            <Badge bg="secondary" className="decision-card-committee">
                                                {committeeCell(d)}
                                            </Badge>
                                        </div>
                                        <div className="decision-card-item">{itemCell(d)}</div>
                                        <div
                                            className="decision-card-decision"
                                            dangerouslySetInnerHTML={{ __html: decisionHtml(d) }}
                                        />
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => navigate('/meeting/' + d.meetingId)}
                                        >
                                            {t("Open Meeting")} →
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </>
            )}
        </>
    );
}
